import { Box } from '@chakra-ui/react'
import { ReactNode, SelectHTMLAttributes, createContext, useContext } from 'react'
import { ChevronDown } from 'lucide-react'

type SelectSize = 'sm' | 'md' | 'lg'

interface NativeSelectRootProps {
  children: ReactNode
  size?: SelectSize
}

interface NativeSelectFieldProps extends SelectHTMLAttributes<HTMLSelectElement> {
  children: ReactNode
}

const SizeContext = createContext<SelectSize>('md')

const SIZE_STYLES: Record<SelectSize, { padding: string; fontSize: string; height: string }> = {
  sm: { padding: '0.375rem 2.5rem 0.375rem 0.75rem', fontSize: '0.875rem', height: '32px' },
  md: { padding: '0.5rem 2.5rem 0.5rem 1rem', fontSize: '1rem', height: '40px' },
  lg: { padding: '0.75rem 2.5rem 0.75rem 1rem', fontSize: '1.125rem', height: '48px' },
}

export function NativeSelectRoot({ children, size = 'md' }: NativeSelectRootProps) {
  return (
    <SizeContext.Provider value={size}>
      <Box position="relative" display="inline-block" w="full">
        {children}
        <Box
          position="absolute"
          right={3}
          top="50%"
          transform="translateY(-50%)"
          pointerEvents="none"
        >
          <ChevronDown size={size === 'sm' ? 16 : 20} />
        </Box>
      </Box>
    </SizeContext.Provider>
  )
}

export function NativeSelectField({ children, ...props }: NativeSelectFieldProps) {
  const size = useContext(SizeContext)
  const sizeStyle = SIZE_STYLES[size]

  return (
    <select
      {...props}
      style={{
        width: '100%',
        padding: sizeStyle.padding,
        height: sizeStyle.height,
        borderRadius: '0.375rem',
        border: '1px solid',
        borderColor: 'var(--chakra-colors-gray-300)',
        fontSize: sizeStyle.fontSize,
        lineHeight: '1.5',
        backgroundColor: 'var(--chakra-colors-white)',
        appearance: 'none',
        WebkitAppearance: 'none',
        MozAppearance: 'none',
        ...props.style,
      }}
    >
      {children}
    </select>
  )
}
